import { theme, type ThemeConfig } from "antd";

/**
 * The one place the back office's look is defined. Every colour, radius,
 * shadow and font size on screen comes from these antd tokens — components
 * use antd (Card, Typography, Tag, Button, Table, ...) and read tokens through
 * `theme.useToken()` when they need a value directly. Tailwind is only used
 * for layout (flex/grid, spacing, sizing, responsive breakpoints).
 *
 * There is a light and a dark theme (ThemeProvider picks one). Both share the
 * brand colours and shapes below; the dark one is antd's dark algorithm with
 * the light-only surface colours left out. To re-tone the whole back office,
 * change `colorPrimary` here.
 */
export type ResolvedTheme = "light" | "dark";

const sharedToken: ThemeConfig["token"] = {
	// --app-font is set per UI language in globals.css (Thai, Cyrillic, CJK)
	fontFamily: "var(--app-font)",
	colorPrimary: "#2563eb",
	colorInfo: "#2563eb",
	colorLink: "#2563eb",
	colorSuccess: "#16a34a",
	colorWarning: "#d97706",
	colorError: "#dc2626",
	borderRadius: 10,
	borderRadiusLG: 16,
	borderRadiusSM: 6,
};

const sharedComponents: ThemeConfig["components"] = {
	Menu: {
		itemBorderRadius: 10,
		itemHeight: 40,
		itemMarginInline: 12,
		iconSize: 16,
		activeBarBorderWidth: 0,
		groupTitleFontSize: 12,
	},
	Card: {
		borderRadiusLG: 16,
		bodyPadding: 20,
		bodyPaddingSM: 16,
	},
	Modal: {
		borderRadiusLG: 16,
	},
	Statistic: {
		titleFontSize: 12,
		contentFontSize: 24,
	},
};

// Sliders (Advanced payout editors): a solid brand track and handle in both
// themes — antd's dark algorithm otherwise draws them in a muddy navy on a
// rail that is barely there.
const sliderShape = {
	railSize: 6,
	handleSize: 14,
	handleSizeHover: 16,
	handleLineWidth: 3,
	handleLineWidthHover: 3,
};

export const lightTheme: ThemeConfig = {
	token: { ...sharedToken, colorBgLayout: "#f3f4f6" },
	components: {
		...sharedComponents,
		Layout: {
			siderBg: "#ffffff",
			headerBg: "#ffffff",
			bodyBg: "#f3f4f6",
		},
		Button: {
			fontWeight: 600,
			primaryShadow: "0 2px 8px rgba(37, 99, 235, 0.2)",
		},
		Table: {
			headerBg: "#f9fafb",
			headerSplitColor: "transparent",
			rowHoverBg: "#f5f8ff",
		},
		Slider: {
			...sliderShape,
			railBg: "#e5e7eb",
			railHoverBg: "#d1d5db",
			trackBg: "#2563eb",
			trackHoverBg: "#1d4ed8",
			handleColor: "#2563eb",
			handleActiveColor: "#1d4ed8",
		},
	},
};

export const darkTheme: ThemeConfig = {
	algorithm: theme.darkAlgorithm,
	token: sharedToken,
	components: {
		...sharedComponents,
		// antd's Layout defaults to its navy (#001529) sider and header; use the
		// dark container colour so the shell matches the cards
		Layout: {
			siderBg: "#141414",
			headerBg: "#141414",
		},
		Button: {
			fontWeight: 600,
			primaryShadow: "none",
		},
		Table: {
			headerSplitColor: "transparent",
		},
		Slider: {
			...sliderShape,
			railBg: "rgba(255, 255, 255, 0.16)",
			railHoverBg: "rgba(255, 255, 255, 0.24)",
			trackBg: "#3b82f6",
			trackHoverBg: "#60a5fa",
			handleColor: "#3b82f6",
			handleActiveColor: "#60a5fa",
		},
	},
};

export const themeFor = (resolved: ResolvedTheme): ThemeConfig =>
	resolved === "dark" ? darkTheme : lightTheme;
